import React, { memo, useCallback } from 'react';

import { PiFileText, PiPlayCircleFill } from 'react-icons/pi';
import { TbDownload } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';

import audioWave from '../../../assets/img/audio-wave.jpg';
import AudioPlayer from '../../../components/AudioPlayer/AudioPlayer';
import BitmovinPlayer from '../../../components/VideoPlayer/BitmovinPlayer/BitmovinPlayer';
import { setGlobalCarouselMediaList } from '../../../context/communityReducer';
import { useAppDispatch } from '../../../shared/hooks';
import { getFileExtension } from '../../../shared/utils';
import { IPostMediaLayout, ISelFile } from '../../../types/communityTypes';

type Props = {
  files: ISelFile[];
  selectedLayout?: IPostMediaLayout;
  renderDummy?: boolean;
  isMessageRender?: boolean;
};

const CreatePostMediaRender: React.FC<Props> = ({
  files,
  selectedLayout = 'carousel',
  renderDummy = false,
  isMessageRender = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClickOnCollage = useCallback(
    (index: number) => {
      if (files?.length > 1 && selectedLayout === 'collage') {
        const search = new URLSearchParams();
        search.append('initialSlide', index.toString());
        navigate({ search: search.toString() });
        dispatch(setGlobalCarouselMediaList(files));
      }
    },
    [dispatch, files, navigate, selectedLayout],
  );

  const renderAttachment = useCallback(
    (file: ISelFile, index?: number) => {
      if (file.type === 'image') {
        return (
          <div
            onClick={() =>
              typeof index === 'number'
                ? handleClickOnCollage(index)
                : undefined
            }
            style={
              isMessageRender
                ? { width: '100%', aspectRatio: 'auto', border: 'none' }
                : {}
            }
            className={`createpost__attachment createpost__attachment__image
              ${
                selectedLayout === 'carousel'
                  ? ''
                  : 'createpost__attachment__collage-image'
              }`}>
            <img src={file.url} alt={file.name} />
          </div>
        );
      } else if (file.type === 'video') {
        if (selectedLayout === 'collage' || renderDummy) {
          return (
            <div
              onClick={() =>
                typeof index === 'number'
                  ? handleClickOnCollage(index)
                  : undefined
              }
              className="createpost__attachment createpost__attachment__video"
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '100%', // This creates a square aspect ratio
              }}>
              <video
                src={file.url}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '50%',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <PiPlayCircleFill
                  style={{
                    fontSize: '24px',
                    color: 'white',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                  }}
                />
              </div>
            </div>
          );
        }

        return (
          <div className="createpost__attachment createpost__attachment__video">
            <BitmovinPlayer
              mediaUrl={file.url}
              isCompressDone
              mediaId={`create-post-video_${file.name}`}
            />
          </div>
        );
      } else if (file.type === 'audio') {
        if (selectedLayout === 'collage' || renderDummy) {
          return (
            <div
              onClick={() =>
                typeof index === 'number'
                  ? handleClickOnCollage(index)
                  : undefined
              }
              className="audio-dummy-img"
              style={{ filter: 'brightness(70%)' }}>
              <PiPlayCircleFill />
              {renderAttachment({
                url: audioWave,
                file: undefined as unknown as File,
                name: 'audioWave',
                ext: '.png',
                type: 'image',
              })}
            </div>
          );
        }
        return (
          <div className="createpost__attachment createpost__attachment__audio">
            <AudioPlayer id={`create-post-audio_${file.url}`} url={file.url} />
          </div>
        );
      } else {
        const ext =
          getFileExtension(file.name as string)?.[1].toLowerCase() || '';
        return (
          <div className="seleted-media_wrapper">
            <div
              className="seleted-media_wrapper_media"
              style={{ width: 'calc(100% - 45px)' }}>
              <div
                className="seleted-media_wrapper_media_file"
                style={{ width: `calc(100% - 52px)` }}>
                <div className="seleted-media_wrapper_media_file_icon">
                  <PiFileText />
                </div>
                <div className="seleted-media_wrapper_media_file_name">
                  <p>{file?.name}</p>
                  {ext ? <span>{ext}</span> : null}
                </div>
              </div>
              {isMessageRender ? (
                <button
                  className="seleted-media_wrapper_media_download"
                  onClick={() => {
                    window.open(file?.url);
                  }}>
                  <TbDownload />
                </button>
              ) : null}
            </div>
          </div>
        );
      }
    },
    [handleClickOnCollage, isMessageRender, renderDummy, selectedLayout],
  );

  const displayMediaInCarousel = useCallback(
    (fileList: ISelFile[]) => {
      return (
        <Slider
          className="mangoMediaCarousel"
          easing="linear"
          arrows
          swipeToSlide
          dots={false}
          slidesToShow={1}
          infinite={false}>
          {fileList.map((eachMedia) => (
            <>
              <div>{renderAttachment(eachMedia)}</div>
            </>
          ))}
        </Slider>
      );
    },
    [renderAttachment],
  );

  const displaySingleMedia = (filesList: ISelFile[]) => {
    return (
      <>
        {filesList.map((eachMedia, index) => (
          <div>{renderAttachment(eachMedia, index)}</div>
        ))}
      </>
    );
  };

  const displayTwoMedia = (filesList: ISelFile[]) => {
    return (
      <div className="two-media_container">
        {filesList.map((eachMedia, index) =>
          renderAttachment(eachMedia, index),
        )}
      </div>
    );
  };

  const displayThreeMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, ...remainingObj] = filesList;
    return (
      <div className="three-media_container">
        <div className="three-media_container_first-media">
          {renderAttachment(firstMediaObj, 0)}
        </div>
        <div className="three-media_container_remaining-media">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 1),
          )}
        </div>
      </div>
    );
  };

  const displayFourMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, secMediaObj, ...remainingObj] = filesList;
    return (
      <div className="four-media_container">
        <div className="four-media_container_wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index),
          )}
        </div>
        <div className="four-media_container_wrapper">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 2),
          )}
        </div>
      </div>
    );
  };

  const displayFiveMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, secMediaObj, ...remainingObj] = filesList;
    return (
      <div className="five-media_container">
        <div className="five-media_container_two-media-wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index),
          )}
        </div>
        <div className="five-media_container_three-media-wrapper">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 2),
          )}
        </div>
      </div>
    );
  };

  const displayFivePlusMedia = (filesList: ISelFile[]) => {
    const [
      firstMediaObj,
      secMediaObj,
      thirdMediaObj,
      fourthMediaObj,
      fifthMediaObj,
      ...remainingObj
    ] = filesList;
    return (
      <div className="five-media_container">
        <div className="five-media_container_two-media-wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index),
          )}
        </div>
        <div className="five-media_container_three-media-wrapper five-media_container_remaining-count">
          {[thirdMediaObj, fourthMediaObj, fifthMediaObj].map(
            (eachMedia, index) => renderAttachment(eachMedia, index + 2),
          )}
          <div
            className="createpost__attachment createpost__attachment__image five-media_container_remaining-count_wrapper"
            onClick={() => handleClickOnCollage(5)}>
            +{remainingObj?.length}
          </div>
        </div>
      </div>
    );
  };

  const displayMediaInCollage = (filesList: ISelFile[]) => {
    switch (filesList?.length) {
      case 0:
        return null;
      case 1:
        return displaySingleMedia(filesList);
      case 2:
        return displayTwoMedia(filesList);
      case 3:
        return displayThreeMedia(filesList);
      case 4:
        return displayFourMedia(filesList);
      case 5:
        return displayFiveMedia(filesList);
      default:
        return displayFivePlusMedia(filesList);
    }
  };

  if (selectedLayout === 'carousel' && files?.length > 1 && !renderDummy) {
    return <div>{displayMediaInCarousel(files)}</div>;
  }
  return <div>{displayMediaInCollage(files)}</div>;
};

export default memo(CreatePostMediaRender);
